/*
 * ZIP helpers. Everything is built with DEFLATE at level 9, which is the
 * strongest compression the ZIP container format itself supports.
 */

import JSZip from "jszip";
import { s as strings } from "./strings";

/** DEFLATE level 9 = maximum compression allowed by the ZIP spec. */
export const MAX_COMPRESSION: JSZip.JSZipFileOptions = {
  compression: "DEFLATE",
  compressionOptions: { level: 9 },
};

/**
 * Outlook rejects a base64 attachment payload larger than this many characters
 * (~20 MB of binary). Checked before we bother handing anything to Office.
 */
export const MAX_BASE64_LENGTH = 27892122;

export interface ZipInput {
  /** File name to use inside the archive. */
  name: string;
  /** Payload as a base64 string, raw bytes, or plain text. Exactly one is used. */
  base64?: string;
  bytes?: Uint8Array;
  text?: string;
  /** Modified date stored in the archive entry. */
  date?: Date;
}

/** Strips characters that are illegal in Windows file names. */
export function sanitizeFileName(name: string, fallback = "file"): string {
  const cleaned = (name || "")
    .replace(/[\\/]/g, "_")
    // eslint-disable-next-line no-control-regex
    .replace(/[<>:"|?*\u0000-\u001f]/g, "")
    .replace(/^\.+/, "")
    .trim()
    .replace(/[. ]+$/, "");
  return cleaned || fallback;
}

/** Returns a name that is not yet in `used`, appending " (2)", " (3)", ... */
export function uniqueName(name: string, used: Set<string>): string {
  if (!used.has(name.toLowerCase())) {
    used.add(name.toLowerCase());
    return name;
  }
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  for (let i = 2; ; i++) {
    const candidate = `${stem} (${i})${ext}`;
    if (!used.has(candidate.toLowerCase())) {
      used.add(candidate.toLowerCase());
      return candidate;
    }
  }
}

/** Makes sure `name` ends in `ext` (case-insensitive). */
export function ensureExtension(name: string, ext: string): string {
  return name.toLowerCase().endsWith(ext.toLowerCase()) ? name : name + ext;
}

/** Number of bytes a base64 string decodes to. */
export function base64Bytes(base64: string): number {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

/**
 * Shortens a file name for display inside a notification, keeping the
 * extension. Never use this for the name actually handed to Outlook.
 */
export function shortenForDisplay(name: string, max = 36): string {
  if (name.length <= max) return name;
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot) : "";
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return stem.slice(0, Math.max(1, max - ext.length - 1)) + "\u2026" + ext;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  const rounded = value < 10 ? value.toFixed(1) : String(Math.round(value));
  return `${rounded.replace(".", strings().decimalSeparator)} ${units[unit]}`;
}

/**
 * Builds a ZIP archive from the given entries and returns it as base64,
 * which is the format Outlook's attachment API expects.
 */
export async function createZipBase64(
  files: ZipInput[],
  onProgress?: (percent: number, currentFile: string) => void
): Promise<string> {
  const zip = new JSZip();
  const used = new Set<string>();

  for (const file of files) {
    const name = uniqueName(sanitizeFileName(file.name), used);
    const options: JSZip.JSZipFileOptions = { ...MAX_COMPRESSION, date: file.date };
    if (file.base64 !== undefined) {
      zip.file(name, file.base64, { ...options, base64: true });
    } else if (file.bytes !== undefined) {
      zip.file(name, file.bytes, { ...options, binary: true });
    } else {
      zip.file(name, file.text ?? "", options);
    }
  }

  return zip.generateAsync(
    {
      type: "base64",
      ...MAX_COMPRESSION,
      // Buffer whole entries: streaming trades compression ratio for memory.
      streamFiles: false,
    },
    (meta) => onProgress?.(meta.percent, meta.currentFile ?? "")
  );
}
